import * as React from "react";
import {
	TextField,
	Button,
	Typography,
	MenuItem,
	Checkbox,
	FormControlLabel,
	InputAdornment,
	Divider,
	CircularProgress
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import StatePicker from "components/StatePicker/StatePicker";
import dayjs from "dayjs";
import { errorsType } from "./ConventionManagementTypes";
import Axios from "axios";
let isLoaded = false;

interface conventionProps {
  conventionId: number;
	title: string;
	tagline: string;
	hashtag: string;
	location: string;
	streetAddress: string;
	city: string;
	state: string;
	zipcode: string;
	startDate: string;
	endDate: string;
	postmarkDate: string;
	defaultPasskeyEventCode: string;
	active: boolean;
	passkeyEventId: number;
	conventionUrl: string;
  	conventionCventMapping: cventMappingProps;
}

interface cventMappingProps {
	conventionId: number;
	eventId: string;
	eventAdmissionItemId: string;
	memberIdCustomFieldId: string;
	localNumberCustomFieldId: number;
	localNameCustomFieldId: string;
	committeeChoice1CustomFieldId: string;
	committeeChoice2CustomFieldId: string;
	committeeChoice3CustomFieldId: string;
	badgeNumberCustomFieldId: string;
	delegateBadgeNumberCustomFieldId: string;
	delegateLocalNumberCustomFieldId: number;
	delegateLocalNameCustomFieldId: string;
	alternativePriorityCustomFieldId: string;
	stateCustomFieldId: string;
	regionCustomFieldId: string;
}

interface cventCustomFieldProps {
  id: string;
  name: string;
}

interface cventEventQuestionProps {
  id: string,
  text: string,
}

interface cventAdmissionItemProps {
  id: string;
  name: string;
}

interface IConventionManagementCreateEditFormProps {
	onSubmit: any;
	currentConvention?: conventionProps;
	onChangeForElements: any;
	errors: errorsType;
	isCventIdSet?: boolean;
}

const ConventionManagementCreateEditForm: React.FC<IConventionManagementCreateEditFormProps> = (props) => {

	const formatDateToRenderDefaultValue = (dateToFormat: string) => {
		return dayjs(dateToFormat).format("YYYY-MM-DD").toString();
	};

  const [cventEventQuestions, setCventEventQuestions] = React.useState<cventEventQuestionProps[]>([]);
  const [cventEvents, setCventEvents] = React.useState<{ id: string; name: string }[]>([]);
  const [cventCustomFields, setCventCustomFields] = React.useState<cventCustomFieldProps[]>([]);
  const [cventAdmissionItems, setCventAdmissionItems] = React.useState<cventAdmissionItemProps[]>([]);

	const getCventEvents = async () => {
		await Axios.get("/api/cvent/events/upcoming")
			.then((resp) => {
				return resp;
			})
			.then((data) => {
				if (isLoaded) {
					setCventEvents([...data.data.payload].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '')));
				}
			})
			.catch((err) => {
				console.error(err);
			});
	};

  const handleEventIdSelectChange = async (event: any) => {
	refreshCventData(event.target.value);
  }

  const getCventEventQuestions = async (eventId: string) => {
		await Axios.get("/api/cvent/events/"+eventId+"/questions")
			.then((resp) => {
				return resp;
			})
			.then((data) => {
				if (isLoaded) {
					setCventEventQuestions([...data.data.payload].sort((a, b) => (a.text ?? '').localeCompare(b.text ?? '')));
				}
			})
			.catch((err) => {
				console.error(err);
			});
	};

  const getCventCustomFields = async () => {
		await Axios.get("/api/cvent/custom-fields/contact")
			.then((resp) => {
				return resp;
			})
			.then((data) => {
				if (isLoaded) {
					setCventCustomFields([...data.data.payload].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
				}
			})
			.catch((err) => {
				console.error(err);
			});
	};

  const getCventAdmissionItems = async (eventId: string) => {
		await Axios.get("/api/cvent/events/"+eventId+"/admission-items")
			.then((resp) => {
				return resp;
			})
			.then((data) => {
				if (isLoaded) {
					setCventAdmissionItems([...data.data.payload].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
				}
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const refreshCventData = async (eventId: string | undefined) => {
		getCventEvents();
		getCventCustomFields();
		if (eventId) {
			getCventEventQuestions(eventId);
			getCventAdmissionItems(eventId);
		}
	}

	React.useEffect(() => {
		isLoaded = true;
		refreshCventData(props.currentConvention?.conventionCventMapping?.eventId);
		return () => {
			isLoaded = false;
		}
	}, [props.currentConvention]);

	return (
		<form onSubmit={props.onSubmit} method={"post"}>
			<Grid container spacing={1}>
				<Grid size={{ xs: 10 }}>
					<TextField
						fullWidth
						id={`convention-title-${
							props.currentConvention ? "update" : "creation"
						}`}
						label="Title of Convention"
						name="title"
						onChange={props.onChangeForElements}
						defaultValue={
							props.currentConvention
								? props.currentConvention.title
								: ""
						}
						autoComplete="conventionTitle"
						margin="normal"
						variant="outlined"
						required
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 2 }} style={{display: "flex", justifyContent : "center"}}>
					<FormControlLabel
						control={
						<Checkbox
							color="primary"
							name={"active"}
							checked={
							props.currentConvention && props.currentConvention.active
								? true
								: false
							}
							onChange={props.onChangeForElements}
						/>
						}
						label="Active"
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<TextField
						fullWidth
						label="Tag Line"
						id={`convention-tagline-${
							props.currentConvention ? "update" : "creation"
						}`}
						onChange={props.onChangeForElements}
						name="tagline"
						autoComplete="tagLine"
						defaultValue={
							props.currentConvention
								? props.currentConvention.tagline
								: ""
						}
						margin="normal"
						variant="outlined"
						required
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<TextField
						fullWidth
						label="Hash Tag"
						id={`convention-hashtag-${
							props.currentConvention ? "update" : "creation"
						}`}
						name="hashtag"
						onChange={props.onChangeForElements}
						defaultValue={
							props.currentConvention
								? props.currentConvention.hashtag
								: ""
						}
						autoComplete="hashTag"
						margin="normal"
						variant="outlined"
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										#
									</InputAdornment>
								),
							},
						}}
						required
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }} style={{"display": "none"}}>
					<TextField
						fullWidth
						label="Passkey Event ID"
						name="passkeyEventId"
						id={`convention-defaultPasskeyEventCode-${
							props.currentConvention ? "update" : "creation"
						}`}
						onChange={props.onChangeForElements}
						defaultValue={
							props.currentConvention
								? props.currentConvention.passkeyEventId
								: ""
						}
						autoComplete="hashTag"
						margin="normal"
						variant="outlined"
						error={props.errors.passkeyEventId.error}
						helperText={
							props.errors.passkeyEventId.error
								? "This field can only contain numbers"
								: ""
						}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }} style={{"display": "none"}}>
					<TextField
						fullWidth
						label="Default Passkey Attendee Code"
						name="defaultPasskeyEventCode"
						id={`convention-defaultPasskeyEventCode-${
							props.currentConvention ? "update" : "creation"
						}`}
						onChange={props.onChangeForElements}
						defaultValue={
							props.currentConvention
								? props.currentConvention
										.defaultPasskeyEventCode
								: ""
						}
						autoComplete="hashTag"
						margin="normal"
						variant="outlined"
					/>
				</Grid>

				<Grid size={{ xs: 12, md: 6 }}>
					<TextField
						fullWidth
						id={`convention-location-${
							props.currentConvention ? "update" : "creation"
						}`}
						label="Location Name"
						onChange={props.onChangeForElements}
						name="location"
						defaultValue={
							props.currentConvention
								? props.currentConvention.location
								: ""
						}
						autoComplete="locationName"
						margin="normal"
						variant="outlined"
						required
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<TextField
						fullWidth
						id={`convention-street-address-${
							props.currentConvention ? "update" : "creation"
						}`}
						label="Street Address"
						name="streetAddress"
						onChange={props.onChangeForElements}
						autoComplete="streetAddress"
						defaultValue={
							props.currentConvention
								? props.currentConvention.streetAddress
								: ""
						}
						margin="normal"
						variant="outlined"
						required
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<TextField
						fullWidth
						id={`convention-city-${
							props.currentConvention ? "update" : "creation"
						}`}
						label="City"
						onChange={props.onChangeForElements}
						name="city"
						defaultValue={
							props.currentConvention
								? props.currentConvention.city
								: ""
						}
						autoComplete="city"
						margin="normal"
						variant="outlined"
						required
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }} className="mt-3">
					<StatePicker
						id={`convention-convention-management-affiliate-state-dropdown-${
							props.currentConvention ? "update" : "creation"
						}`}
						label={"State"}
						onChange={props.onChangeForElements}
						defaultValue={
							props.currentConvention
								? props.currentConvention.state
								: ""
						}
						displayDefaultValue={
							props.currentConvention ? true : false
						}
						name={"state"}
						required={true}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<TextField
						fullWidth
						id={`convention-zipcode-${
							props.currentConvention ? "update" : "creation"
						}`}
						label="Zipcode"
						name="zipcode"
						onChange={props.onChangeForElements}
						autoComplete="zipcode"
						defaultValue={
							props.currentConvention
								? props.currentConvention.zipcode
								: ""
						}
						margin="normal"
						variant="outlined"
						required
						error={props.errors.zipcode.error}
						helperText={
							props.errors.zipcode.error
								? "Enter a valid zipcode"
								: ""
						}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<TextField
						fullWidth
						id={`convention-start-date-${
							props.currentConvention ? "update" : "creation"
						}`}
						label="Start Date"
						onChange={props.onChangeForElements}
						name="startDate"
						autoComplete="startDate"
						type="date"
						defaultValue={
							props.currentConvention
								? formatDateToRenderDefaultValue(
										props.currentConvention.startDate
								  )
								: ""
						}
						margin="normal"
						slotProps={{
							inputLabel: {
								shrink: true,
								required: true,
							},
						}}
						variant="outlined"
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<TextField
						fullWidth
						id={`convention-end-date-${
							props.currentConvention ? "update" : "creation"
						}`}
						label="End Date"
						name="endDate"
						autoComplete="endDate"
						onChange={props.onChangeForElements}
						type="date"
						margin="normal"
						defaultValue={
							props.currentConvention
								? formatDateToRenderDefaultValue(
										props.currentConvention.endDate
								  )
								: ""
						}
						required
						slotProps={{
							inputLabel: {
								shrink: true,
							},
						}}
						variant="outlined"
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<TextField
						fullWidth
						id={`convention-postmark-date-${
							props.currentConvention ? "update" : "creation"
						}`}
						label="Postmark Date"
						name="postmarkDate"
						onChange={props.onChangeForElements}
						autoComplete="postmarkDate"
						type="date"
						margin="normal"
						defaultValue={
							props.currentConvention
								? formatDateToRenderDefaultValue(
										props.currentConvention.postmarkDate
								  )
								: ""
						}
						required
						slotProps={{
							inputLabel: {
								shrink: true,
							},
						}}
						variant="outlined"
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
					<TextField
						fullWidth
						id={`convention-url-${
							props.currentConvention ? "update" : "creation"
						}`}
						label="Convention URL"
						name="conventionUrl"
						onChange={props.onChangeForElements}
						defaultValue={
							props.currentConvention
								? props.currentConvention.conventionUrl
								: ""
						}
						autoComplete="conventionUrl"
						margin="normal"
						variant="outlined"
						required
					/>
				</Grid>

				<Grid size={{ xs: 12 }} className="mt-3 mb-3">
					<hr className="MuiDivider-root MuiDivider-middle" />
				</Grid>
				<Grid size={{ xs: 12 }}>
					<Typography variant="subtitle1">
						Cvent Configurations
					</Typography>
					{
						(!cventEvents || !cventCustomFields || !cventAdmissionItems) ? (
							<Grid size={{ xs: 12 }}>
							<Grid size={{ xs: 6 }}>
								Loading Cvent Configuration Items
							</Grid>
							<Grid size={{ xs: 6 }}>
								<CircularProgress></CircularProgress>
							</Grid>
							</Grid>
						) : (null)
					}
				</Grid>
				<Grid size={{ xs: 12, md: 6 }}>
          { cventEvents ? (
					<TextField
						select
						fullWidth
						id={`convention-cvent-event-id-${
							props.currentConvention ? "update" : "creation"
						}`}
						label="Cvent Mapping"
						name="eventId"
						onChange={handleEventIdSelectChange}
						margin="normal"
						defaultValue={
							props.currentConvention?.conventionCventMapping?.eventId
								? props.currentConvention!.conventionCventMapping!.eventId
								: 0
						}
						required
						variant="outlined"
					>
						{cventEvents ? (
							cventEvents.map((event: any) => {
								const id = event.id;
								const title = `${
									event.title
								} (${event.start.substring(0, 10)})`;
								return (
									<MenuItem
										value={id}
										key={`${event.title}_type`}
									>
										{title}
									</MenuItem>
								);
							})
						) : (
							<MenuItem disabled>Loading Events...</MenuItem>
						)}
					</TextField>
          ) : (
			""
          )
        }
        </Grid>

        {
          (cventEvents && cventCustomFields && cventAdmissionItems) ? (
            <>

			<Grid size={{ xs: 6 }} style={{display: "flex", justifyContent : "center"}}>

				</Grid>
              <Grid size={{ xs: 12, md: 6 }}>
							<TextField
								select
								fullWidth
								id={`convention-cvent-addmission-item-${
									props.currentConvention
										? "update"
										: "creation"
								}`}
								label="Cvent Admission Item"
								name="eventAdmissionItemId"
								onChange={props.onChangeForElements}
								margin="normal"
								defaultValue={
									props.currentConvention?.conventionCventMapping?.eventAdmissionItemId
										? props.currentConvention?.conventionCventMapping?.eventAdmissionItemId
										: ""
								}
								required
								variant="outlined"
							>
              {cventAdmissionItems ? (
                cventAdmissionItems.map((question: cventAdmissionItemProps) => {
                  return (
                    <MenuItem
                      value={question.id}
                      key={`${question.name}_type`}
                    >
                      {question.name}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem disabled>Loading Event Administrative Items...</MenuItem>
              )}
              </TextField>
              </Grid>

				<Grid size={{ xs: 12, md: 6 }}>
				<TextField
									select
									fullWidth
									id={`convention-memberid-customfield-${
										props.currentConvention
											? "update"
											: "creation"
									}`}
									label="Member ID"
									name="memberIdCustomFieldId"
									onChange={props.onChangeForElements}
									margin="normal"
									defaultValue={
										props.currentConvention?.conventionCventMapping?.memberIdCustomFieldId
											? props.currentConvention?.conventionCventMapping?.memberIdCustomFieldId
											: ""
									}
									required
									variant="outlined"
								>
				{cventCustomFields ? (
					cventCustomFields.map((question: cventCustomFieldProps) => {
					return (
						<MenuItem
						value={question.id}
						key={`${question.name}_type`}
						>
						{question.name}
						</MenuItem>
					);
					})
				) : (
					<MenuItem disabled>Loading Event Custom Fields...</MenuItem>
				)}
				</TextField>
				</Grid>





				<Grid size={{ xs: 12, md: 4 }} style={{"display": "none"}}>
							<TextField
								select
								fullWidth
								id={`convention-representing-local-name-${
									props.currentConvention
										? "update"
										: "creation"
								}`}
								label="Representing Local Name"
								name="localNameCustomFieldId"
								onChange={props.onChangeForElements}
								margin="normal"
								defaultValue={
									props.currentConvention?.conventionCventMapping?.localNameCustomFieldId
										? props.currentConvention?.conventionCventMapping?.localNameCustomFieldId
										: ""
								}
								variant="outlined"
							>
				{cventEventQuestions ? (
				cventEventQuestions.map((question: cventEventQuestionProps) => {
					return (
					<MenuItem
						value={question.id}
						key={`${question.text}_type`}
					>
						{question.text}
					</MenuItem>
					);
				})
				) : (
				<MenuItem disabled>Loading Custom Fields...</MenuItem>
				)}
				</TextField>
				</Grid>

				<Grid size={{ xs: 12, md: 4 }} style={{"display": "none"}}>
							<TextField
								select
								fullWidth
								id={`convention-representing-local-number-${
									props.currentConvention
										? "update"
										: "creation"
								}`}
								label="Representing Local Number"
								name="localNumberCustomFieldId"
								onChange={props.onChangeForElements}
								margin="normal"
								defaultValue={
									props.currentConvention?.conventionCventMapping?.localNumberCustomFieldId
										? props.currentConvention?.conventionCventMapping?.localNumberCustomFieldId
										: ""
								}
								variant="outlined"
							>
				{cventEventQuestions ? (
				cventEventQuestions.map((question: cventEventQuestionProps) => {
					return (
					<MenuItem
						value={question.id}
						key={`${question.text}_type`}
					>
						{question.text}
					</MenuItem>
					);
				})
				) : (
				<MenuItem disabled>Loading Custom Fields...</MenuItem>
				)}
				</TextField>
				</Grid>

				<Grid size={{ xs: 12, md: 4 }} style={{"display": "none"}}>
					<TextField
						select
						fullWidth
						id={`convention-badge${
							props.currentConvention
								? "update"
								: "creation"
						}`}
						label="Badge Number"
						name="badgeNumberCustomFieldId"
						onChange={props.onChangeForElements}
						margin="normal"
						defaultValue={
							props.currentConvention?.conventionCventMapping?.badgeNumberCustomFieldId
								? props.currentConvention?.conventionCventMapping?.badgeNumberCustomFieldId
								: ""
						}
						variant="outlined"
					>
					{
					cventEventQuestions ? (cventEventQuestions.map((question: cventEventQuestionProps) => {
							return (<MenuItem value={question.id} key={`${question.text}_type`}>
										{question.text}
									</MenuItem>);
						})
						) : (
							<MenuItem disabled>Loading Custom Fields...</MenuItem>
						)
					}
					</TextField>
				</Grid>

				<Grid size={{ xs: 12, md: 12 }}>
				</Grid>

				<Grid size={{ xs: 12, md: 12 }}>
					<Divider variant="middle" />
				</Grid>

				<Grid size={{ xs: 12, md: 12 }} style={{ paddingTop: "10px" }}>
					<Typography variant="subtitle1" >
						Mappings
					</Typography>
				</Grid>

				<Grid size={{ xs: 12, md: 4 }}>
							<TextField
											select
											fullWidth
											id={`convention-representing-local-name-${
												props.currentConvention
													? "update"
													: "creation"
											}`}
											label="Alternative Priority"
											name="alternativePriorityCustomFieldId"
											onChange={props.onChangeForElements}
											margin="normal"
											defaultValue={
												props.currentConvention?.conventionCventMapping?.alternativePriorityCustomFieldId
													? props.currentConvention?.conventionCventMapping?.alternativePriorityCustomFieldId
													: ""
											}
											required
											variant="outlined"
										>
							{cventEventQuestions ? (
							cventEventQuestions.map((question: cventEventQuestionProps) => {
								return (
								<MenuItem
									value={question.id}
									key={`${question.text}_type`}
								>
									{question.text}
								</MenuItem>
								);
							})
							) : (
							<MenuItem disabled>Loading Custom Fields...</MenuItem>
							)}
							</TextField>
				</Grid>

				<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								select
								fullWidth
								id={`convention-representing-state-${
									props.currentConvention
										? "update"
										: "creation"
								}`}
								label="Representing State"
								name="stateCustomFieldId"
								onChange={props.onChangeForElements}
								margin="normal"
								defaultValue={
									props.currentConvention?.conventionCventMapping?.stateCustomFieldId
										? props.currentConvention?.conventionCventMapping?.stateCustomFieldId
										: ""
									}
								variant="outlined"
							>
							{cventEventQuestions ? (
							cventEventQuestions.map((question: cventEventQuestionProps) => {
								return (
								<MenuItem
									value={question.id}
									key={`${question.text}_type`}
								>
									{question.text}
								</MenuItem>
								);
							})
							) : (
							<MenuItem disabled>Loading Custom Fields...</MenuItem>
							)}
							</TextField>
				</Grid>

				<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								select
								fullWidth
								id={`convention-representing-region-${
									props.currentConvention
										? "update"
										: "creation"
								}`}
								label="Representing Region"
								name="regionCustomFieldId"
								onChange={props.onChangeForElements}
								margin="normal"
								defaultValue={
									props.currentConvention?.conventionCventMapping?.regionCustomFieldId
										? props.currentConvention?.conventionCventMapping?.regionCustomFieldId
										: ""
									}
								variant="outlined"
							>
							{cventEventQuestions ? (
							cventEventQuestions.map((question: cventEventQuestionProps) => {
								return (
								<MenuItem
									value={question.id}
									key={`${question.text}_type`}
								>
									{question.text}
								</MenuItem>
								);
							})
							) : (
							<MenuItem disabled>Loading Custom Fields...</MenuItem>
							)}
							</TextField>
				</Grid>

				<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								select
								fullWidth
								id={`convention-representing-local-name-${
									props.currentConvention
										? "update"
										: "creation"
								}`}
								label="Representing Local Name"
								name="delegateLocalNameCustomFieldId"
								onChange={props.onChangeForElements}
								margin="normal"
								defaultValue={
									props.currentConvention?.conventionCventMapping?.delegateLocalNameCustomFieldId
										? props.currentConvention?.conventionCventMapping?.delegateLocalNameCustomFieldId
										: ""
								}
								required
								variant="outlined"
							>
				{cventEventQuestions ? (
				cventEventQuestions.map((question: cventEventQuestionProps) => {
					return (
					<MenuItem
						value={question.id}
						key={`${question.text}_type`}
					>
						{question.text}
					</MenuItem>
					);
				})
				) : (
				<MenuItem disabled>Loading Custom Fields...</MenuItem>
				)}
				</TextField>
				</Grid>

				<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								select
								fullWidth
								id={`convention-representing-local-number-${
									props.currentConvention
										? "update"
										: "creation"
								}`}
								label="Representing Local Number"
								name="delegateLocalNumberCustomFieldId"
								onChange={props.onChangeForElements}
								margin="normal"
								defaultValue={
									props.currentConvention?.conventionCventMapping?.delegateLocalNumberCustomFieldId
										? props.currentConvention?.conventionCventMapping?.delegateLocalNumberCustomFieldId
										: ""
								}
								required
								variant="outlined"
							>
				{cventEventQuestions ? (
				cventEventQuestions.map((question: cventEventQuestionProps) => {
					return (
					<MenuItem
						value={question.id}
						key={`${question.text}_type`}
					>
						{question.text}
					</MenuItem>
					);
				})
				) : (
				<MenuItem disabled>Loading Custom Fields...</MenuItem>
				)}
				</TextField>
				</Grid>

				<Grid size={{ xs: 12, md: 4 }}>
					<TextField
						select
						fullWidth
						id={`convention-badge${
							props.currentConvention
								? "update"
								: "creation"
						}`}
						label="Badge Number"
						name="delegateBadgeNumberCustomFieldId"
						onChange={props.onChangeForElements}
						margin="normal"
						defaultValue={
							props.currentConvention?.conventionCventMapping?.delegateBadgeNumberCustomFieldId
								? props.currentConvention?.conventionCventMapping?.delegateBadgeNumberCustomFieldId
								: ""
						}
						required
						variant="outlined"
					>
					{
					cventEventQuestions ? (cventEventQuestions.map((question: cventEventQuestionProps) => {
							return (<MenuItem value={question.id} key={`${question.text}_type`}>
										{question.text}
									</MenuItem>);
						})
						) : (
							<MenuItem disabled>Loading Custom Fields...</MenuItem>
						)
					}
					</TextField>
				</Grid>

				<Grid size={{ xs: 12, md: 4 }}>
					<TextField
						select
						fullWidth
						id={`convention-committee-choice-1-${
							props.currentConvention
								? "update"
								: "creation"
						}`}
						label="Committee Choice 1"
						name="committeeChoice1CustomFieldId"
						onChange={props.onChangeForElements}
						margin="normal"
						defaultValue={
							props.currentConvention?.conventionCventMapping?.committeeChoice1CustomFieldId
								? props.currentConvention?.conventionCventMapping?.committeeChoice1CustomFieldId
								: ""
						}
						required
						variant="outlined"
					>
				{cventEventQuestions ? (
				cventEventQuestions.map((question: cventEventQuestionProps) => {
					return (
					<MenuItem
						value={question.id}
						key={`${question.text}_type`}
					>
						{question.text}
					</MenuItem>
					);
				})
				) : (
				<MenuItem disabled>Loading Event Questions...</MenuItem>
				)}
				</TextField>
				</Grid>

				<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								select
								fullWidth
								id={`convention-committee-choice-2-${
									props.currentConvention
										? "update"
										: "creation"
								}`}
								label="Committee Choice 2"
								name="committeeChoice2CustomFieldId"
								onChange={props.onChangeForElements}
								margin="normal"
								defaultValue={
									props.currentConvention?.conventionCventMapping?.committeeChoice2CustomFieldId
										? props.currentConvention?.conventionCventMapping?.committeeChoice2CustomFieldId
										: ""
								}
								required
								variant="outlined"
							>
				{cventEventQuestions ? (
				cventEventQuestions.map((question: cventEventQuestionProps) => {
					return (
					<MenuItem
						value={question.id}
						key={`${question.text}_type`}
					>
						{question.text}
					</MenuItem>
					);
				})
				) : (
				<MenuItem disabled>Loading Event Questions...</MenuItem>
				)}
				</TextField>
				</Grid>

				<Grid size={{ xs: 12, md: 4 }}>
					<TextField
						select
						fullWidth
						id={`convention-committee-choice-3-${
							props.currentConvention
								? "update"
								: "creation"
						}`}
						label="Committee Choice 3"
						name="committeeChoice3CustomFieldId"
						onChange={props.onChangeForElements}
						margin="normal"
						defaultValue={
							props.currentConvention?.conventionCventMapping?.committeeChoice3CustomFieldId
								? props.currentConvention?.conventionCventMapping?.committeeChoice3CustomFieldId
								: ""
						}
						required
						variant="outlined"
					>
					{
					cventEventQuestions ? (cventEventQuestions.map((question: cventEventQuestionProps) => {
							return (<MenuItem value={question.id} key={`${question.text}_type`}>
										{question.text}
									</MenuItem>);
						})
						) : (
							<MenuItem disabled>Loading Event Questions...</MenuItem>
						)
					}
					</TextField>
				</Grid>
				
			</>
				) : ("")
			}
				<Grid size={{ xs: 12, md: 2 }} style={{ paddingTop: "10px" }}>
					<Button
						fullWidth
						className="btn"
						size="large"
						color="primary"
						variant="contained"
						type="submit"
						disabled={
							props.errors.zipcode.error ||
							props.errors.passkeyEventId.error
						}
					>
						{`${
							props.currentConvention ? "Update" : "Create"
						} Convention`}
					</Button>
				</Grid>
			</Grid>
		</form>
	);
};

export default ConventionManagementCreateEditForm;
